import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatRUT } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, KeyRound, Fingerprint } from "lucide-react";
import { startAuthentication, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { PASSKEY_FLAG } from "@/components/PasskeySettings";
import { toast } from "sonner";
import { SupportLine } from "@/components/SupportContact";

export default function Login() {
  const navigate = useNavigate();
  const { loginWith } = useAuth();
  const [rut, setRut] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // login | verify
  const [ticket, setTicket] = useState(null);
  const [emailHint, setEmailHint] = useState("");
  const [code, setCode] = useState("");

  const finish = (data) => {
    loginWith(data.token, data.user);
    const staff = ["evaluator", "admin", "superadmin"].includes(data.user.role);
    navigate(staff ? "/admin" : "/panel");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { rut, password });
      if (data.token) { finish(data); return; }
      if (data.mfa_required) {
        setTicket(data.ticket);
        setEmailHint(data.email_hint || "");
        setMode("verify");
        toast.success("Te enviamos un código a tu correo.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyEmail = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/email/verify", { ticket, code });
      finish(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const showPasskey = browserSupportsWebAuthn() && localStorage.getItem(PASSKEY_FLAG) === "1";

  const passkeyLogin = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/passkeys/login/options");
      const credential = await startAuthentication({ optionsJSON: JSON.parse(data.options) });
      const res = await api.post("/auth/passkeys/login/verify", { ceremony: data.ceremony, credential });
      finish(res.data);
    } catch (err) {
      if (err.response?.status === 401) localStorage.removeItem(PASSKEY_FLAG);
      toast.error(err.response ? formatApiError(err.response.data?.detail) : "Ingreso con Face ID cancelado.");
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    try {
      await api.post("/auth/mfa/email/resend", { ticket });
      toast.success("Código reenviado a tu correo.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-md fade-in">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <img src="/eim-logo.png" alt="EIM" className="w-11 h-11 object-contain" />
          <span className="font-heading font-extrabold text-xl text-primary">EIM Becas</span>
        </div>
        <div className="bg-white rounded-2xl border border-border p-8">
          {mode === "login" && (
            <>
              <h1 className="text-2xl font-bold mb-1">Ingresar</h1>
              <p className="text-muted-foreground mb-6">Accede con tu RUT y contraseña.</p>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <Label className="text-sm font-semibold uppercase tracking-wide text-slate-600">RUT</Label>
                  <Input data-testid="input-rut" className="h-14 text-lg rounded-xl mt-2" placeholder="12.345.678-9"
                    value={rut} onChange={(e) => setRut(formatRUT(e.target.value))} required />
                </div>
                <div>
                  <Label className="text-sm font-semibold uppercase tracking-wide text-slate-600">Contraseña</Label>
                  <Input data-testid="input-password" type="password" className="h-14 text-lg rounded-xl mt-2"
                    value={password} onChange={(e) => setPassword(e.target.value)} required />
                </div>
                <Button type="submit" disabled={loading} className="btn-lift w-full h-14 text-lg rounded-xl" data-testid="btn-login-submit">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Ingresar"}
                </Button>
              </form>
              {showPasskey && (
                <Button type="button" variant="outline" onClick={passkeyLogin} disabled={loading}
                  className="w-full h-14 text-lg rounded-xl border-2 mt-3" data-testid="btn-passkey-login">
                  <Fingerprint className="w-5 h-5 mr-2" /> Ingresar con Face ID
                </Button>
              )}
              <div className="flex justify-between mt-5 text-sm">
                <Link to="/recuperar" className="text-primary font-semibold hover:underline" data-testid="link-forgot">¿Olvidaste tu contraseña?</Link>
                <Link to="/registro" className="text-primary font-semibold hover:underline" data-testid="link-register">Crear cuenta</Link>
              </div>
            </>
          )}

          {mode === "verify" && (
            <div className="space-y-5">
              <div className="flex items-center gap-2"><KeyRound className="w-6 h-6 text-accent" /><h1 className="text-2xl font-bold">Verificación por correo</h1></div>
              <p className="text-muted-foreground text-sm">Enviamos un código de 6 dígitos a tu correo{emailHint ? <> (<span className="font-medium">{emailHint}</span>)</> : ""}. Ingrésalo para completar tu ingreso.</p>
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wide text-slate-600">Código</Label>
                <Input data-testid="mfa-code" className="h-14 text-lg rounded-xl mt-2 tracking-widest text-center" maxLength={6}
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} autoFocus />
              </div>
              <Button onClick={verifyEmail} disabled={loading || code.length < 6}
                className="btn-lift w-full h-14 text-lg rounded-xl" data-testid="btn-mfa-verify">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verificar e ingresar"}
              </Button>
              <button type="button" onClick={resendCode} className="text-sm text-primary font-semibold hover:underline w-full text-center" data-testid="btn-mfa-resend">
                Reenviar código
              </button>
            </div>
          )}
        </div>
        <SupportLine className="mt-6" />
      </div>
    </div>
  );
}
